/**
 * A device's scan-run history (release 3.2 telemetry item 3): each run with
 * its start and end, the files it saw, read and skipped and why, the findings
 * it reported, the CPU and memory it used against the ceiling, and its
 * outcome. Opening a run lists the findings it reported (laptops), by title
 * and finding id.
 *
 * Absent is never 0: a run reported by an agent older than 3.2 carries no
 * findings count or CPU, and says "not reported".
 */

import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, History, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { problemDetail } from '@/lib/api';
import {
  listEdgeScanRunFindings,
  listEdgeScanRuns,
  type EdgeScanRun,
  type EdgeScanRunFindings,
} from '@/lib/edge-telemetry-api';

import { NOT_REPORTED } from './edge-measurement-format';
import { reasonText } from './ScanCoverageCard';
import { formatDuration, formatMemory, formatPercent } from './scan-resources-format';

const INDIAN = new Intl.NumberFormat('en-IN');

export function outcomeBadge(outcome: string): { label: string; variant: 'success' | 'critical' | 'warning' | 'glass' } {
  switch (outcome) {
    case 'succeeded':
      return { label: 'succeeded', variant: 'success' };
    case 'failed':
      return { label: 'failed', variant: 'critical' };
    case 'paused':
      return { label: 'paused, did not scan', variant: 'warning' };
    case 'deferred':
      // Release 8.1: the laptop waited an hour (battery, a presentation, a busy disk) and
      // ended the scan early; the rest is scanned at the next scheduled scan.
      return { label: 'ended early, rest next scan', variant: 'warning' };
    default:
      return { label: outcome.replace(/_/g, ' '), variant: 'glass' };
  }
}

/** Release 3.6 T-29: a read-failure class in words (the agent's fixed vocabulary). */
export function readFailureTitle(cls: string): string {
  switch (cls) {
    case 'permission-denied':
      return 'Permission denied (the scan account lacks SELECT, or SELECT is denied to it)';
    case 'not-found':
      return 'Not found (the table or column is gone, or the account cannot see it)';
    case 'timeout':
      return 'Timed out (past the statement timeout, or waiting on a lock)';
    case 'connection-failed':
      return 'Connection failed';
    case 'decode-failed':
      return 'A value could not be read as text';
    case 'classifier-failed':
      return 'Classification failed';
    default:
      return 'Other server error';
  }
}

/** "1,240 seen: 1,200 read (3 partly), 40 skipped" or a database run's targets. */
export function filesText(r: EdgeScanRun): string {
  if (r.filesSeen != null && r.filesScanned != null && r.filesSkipped != null) {
    const partly = r.filesPartlyRead ? ` (${INDIAN.format(r.filesPartlyRead)} partly)` : '';
    return `${INDIAN.format(r.filesSeen)} seen: ${INDIAN.format(r.filesScanned)} read${partly}, ${INDIAN.format(r.filesSkipped)} skipped`;
  }
  if (r.targetsTotal != null) {
    const failed = r.targetsFailed ? `, ${INDIAN.format(r.targetsFailed)} failed` : '';
    const cols = r.columnsScanned != null ? `; ${INDIAN.format(r.columnsScanned)} columns read` : '';
    return `${INDIAN.format(r.targetsTotal)} target${r.targetsTotal === 1 ? '' : 's'}${failed}${cols}`;
  }
  return NOT_REPORTED;
}

export function skippedReasonTitle(reason: string): string {
  return reason === 'not_scanned_by_design'
    ? 'Types the agent never reads (archives, programs and similar)'
    : reasonText(reason).title;
}

/** "12 (12 delivered)", "12 (3 not delivered)", or not reported. */
export function findingsText(r: EdgeScanRun): string {
  if (r.findingsFound == null) return NOT_REPORTED;
  const lost = r.findingsUndelivered ?? 0;
  if (lost > 0) return `${INDIAN.format(r.findingsFound)} (${INDIAN.format(lost)} not delivered)`;
  return INDIAN.format(r.findingsFound);
}

/** "avg 3.2%, peak 4.9% (ceiling 5%)". */
export function cpuText(r: EdgeScanRun): string {
  if (r.cpuAvgPercent == null && r.cpuPeakPercent == null) return NOT_REPORTED;
  const ceiling = r.cpuCeilingPercent != null ? ` (ceiling ${r.cpuCeilingPercent}%)` : '';
  return `avg ${formatPercent(r.cpuAvgPercent)}, peak ${formatPercent(r.cpuPeakPercent)}${ceiling}`;
}

function RunFindings({ podRowId, run }: { podRowId: string; run: EdgeScanRun }) {
  const [data, setData] = useState<EdgeScanRunFindings | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    listEdgeScanRunFindings(podRowId, run.id)
      .then((d) => alive && setData(d))
      .catch((e: unknown) => alive && setError(problemDetail(e, 'Could not load the findings of this run.')));
    return () => {
      alive = false;
    };
  }, [podRowId, run.id]);
  if (error) return <p className="text-xs text-destructive">{error}</p>;
  if (!data) return <Loader2 className="h-3.5 w-3.5 animate-spin" />;
  if (!data.scoped) {
    return (
      <p className="text-xs text-muted-foreground">
        This device&apos;s findings are not listed per run (it is not a laptop); the run&apos;s counts above stand.
      </p>
    );
  }
  if (data.findings.length === 0) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="run-findings-empty">
        No finding currently carries this run: it reported none, or a later scan reported them again.
      </p>
    );
  }
  return (
    <ul className="space-y-1 text-xs" data-testid="run-findings">
      {data.findings.map((f) => (
        <li key={f.id} className="flex flex-wrap items-baseline gap-2">
          <Badge variant={(['critical', 'high', 'medium', 'low'] as const).find((s) => s === f.severity) ?? 'glass'} className="text-[10px]">
            {f.severity}
          </Badge>
          <span>{f.title}</span>
          <code className="font-mono text-[10px] text-muted-foreground">{f.id}</code>
          {f.asset && <code className="font-mono text-[11px] text-muted-foreground break-all">{f.asset}</code>}
        </li>
      ))}
      {data.truncated && <li className="text-muted-foreground">Showing the first 200.</li>}
    </ul>
  );
}

export function ScanHistoryTab({ podRowId }: { podRowId: string }) {
  const [runs, setRuns] = useState<EdgeScanRun[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(
    async (after: string | null) => {
      setBusy(true);
      setError(null);
      try {
        const page = await listEdgeScanRuns(podRowId, after);
        setRuns((prev) => (after && prev ? [...prev, ...page.runs] : page.runs));
        setCursor(page.nextCursor);
      } catch (e) {
        setError(problemDetail(e, 'Could not load the scan history.'));
      } finally {
        setBusy(false);
      }
    },
    [podRowId],
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  return (
    <div className="space-y-2" data-testid="scan-history">
      <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
        <History className="h-3.5 w-3.5" /> Every scan this device reported, newest first, as the agent measured it.
        Runs appear once the agent is on release 3.2; the runs it finished before that upgrade (up to the newest 10)
        are included, without CPU, findings counts or scan ids. An older agent sends no runs.
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      {runs === null ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : runs.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="scan-history-empty">
          No scan runs reported yet. An agent from release 3.2 sends each finished scan on its next check-in.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1 pr-2 font-medium" />
                <th className="py-1 pr-2 font-medium">Started</th>
                <th className="py-1 pr-2 font-medium">Took</th>
                <th className="py-1 pr-2 font-medium">Outcome</th>
                <th className="py-1 pr-2 font-medium">Files</th>
                <th className="py-1 pr-2 font-medium">Findings</th>
                <th className="py-1 pr-2 font-medium">CPU</th>
                <th className="py-1 font-medium">Memory</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => {
                const o = outcomeBadge(r.outcome);
                const isOpen = open === r.id;
                return (
                  <FragmentRow
                    key={r.id}
                    run={r}
                    isOpen={isOpen}
                    onToggle={() => setOpen(isOpen ? null : r.id)}
                    outcome={o}
                    podRowId={podRowId}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {cursor && (
        <Button size="sm" variant="glass" onClick={() => void load(cursor)} disabled={busy}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null} Older runs
        </Button>
      )}
    </div>
  );
}

function FragmentRow({
  run: r,
  isOpen,
  onToggle,
  outcome,
  podRowId,
}: {
  run: EdgeScanRun;
  isOpen: boolean;
  onToggle: () => void;
  outcome: ReturnType<typeof outcomeBadge>;
  podRowId: string;
}) {
  return (
    <>
      <tr className="border-t border-border/50 align-top cursor-pointer hover:bg-muted/20" onClick={onToggle} data-testid={`scan-run-${r.runId}`}>
        <td className="py-1.5 pr-1">{isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</td>
        <td className="py-1.5 pr-2 whitespace-nowrap">{new Date(r.startedAt ?? r.finishedAt).toLocaleString()}</td>
        <td className="py-1.5 pr-2 whitespace-nowrap">{r.durationSeconds != null ? formatDuration(r.durationSeconds) : NOT_REPORTED}</td>
        <td className="py-1.5 pr-2">
          <Badge variant={outcome.variant} className="text-[10px]">
            {outcome.label}
          </Badge>
        </td>
        <td className="py-1.5 pr-2">{filesText(r)}</td>
        <td className="py-1.5 pr-2 tabular-nums">{findingsText(r)}</td>
        <td className="py-1.5 pr-2 whitespace-nowrap">{cpuText(r)}</td>
        <td className="py-1.5 whitespace-nowrap">{formatMemory(r.memPeakMb)}</td>
      </tr>
      {isOpen && (
        <tr className="border-t border-border/30">
          <td />
          <td colSpan={7} className="py-2 space-y-2">
            <div className="text-[11px] text-muted-foreground">
              Ended {new Date(r.finishedAt).toLocaleString()}. Run {r.runId}
              {r.scanIds.length > 0 ? `; findings sent under scan ${r.scanIds.join(', ')}` : ''}.
            </div>
            {r.detail && <p className="text-xs">{r.detail}</p>}
            {r.skipped && r.skipped.length > 0 && (
              <div className="space-y-0.5" data-testid="scan-run-skipped">
                <div className="text-[11px] font-medium">Skipped, and why</div>
                {r.skipped.map((s) => (
                  <div key={s.reason} className="flex justify-between gap-2 text-xs">
                    <span>{skippedReasonTitle(s.reason)}</span>
                    <span className="tabular-nums">{INDIAN.format(s.count)}</span>
                  </div>
                ))}
              </div>
            )}
            {r.failedReads && r.failedReads.total > 0 && (
              <div className="space-y-0.5" data-testid="scan-run-failed-reads">
                <div className="text-[11px] font-medium text-warning">
                  Columns that could not be read ({INDIAN.format(r.failedReads.total)}); they are read again next run
                </div>
                {Object.entries(r.failedReads.byClass)
                  .filter((e): e is [string, number] => typeof e[1] === 'number' && e[1] > 0)
                  .map(([cls, count]) => (
                    <div key={cls} className="flex justify-between gap-2 text-xs">
                      <span>{readFailureTitle(cls)}</span>
                      <span className="tabular-nums">{INDIAN.format(count)}</span>
                    </div>
                  ))}
                {(r.failedReads.columns ?? []).map((c, i) => (
                  <div key={i} className="text-[11px] text-muted-foreground break-all">
                    {c.column}: {readFailureTitle(c.class)}
                  </div>
                ))}
                {(r.failedReads.columns?.length ?? 0) < r.failedReads.total && (
                  <div className="text-[11px] text-muted-foreground">
                    The first {r.failedReads.columns?.length ?? 0} are listed; the agent&apos;s access log has every one.
                  </div>
                )}
              </div>
            )}
            <div className="text-[11px] font-medium">Findings this run reported</div>
            <RunFindings podRowId={podRowId} run={r} />
          </td>
        </tr>
      )}
    </>
  );
}
